import type { SessionUser } from '@/auth/types';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { m } from '@/locale/paraglide/messages';
import { IconSelector, IconUser } from '@tabler/icons-react';
import { lazy, Suspense, useState } from 'react';

const LazySidebarUserMenu = lazy(() =>
  import('@/components/layout/sidebar-user-menu').then((module) => ({
    default: module.SidebarUser,
  }))
);

interface SidebarUserProps {
  user?: SessionUser | null;
  className?: string;
}

export function SidebarUser({ user }: SidebarUserProps) {
  const [menuRequested, setMenuRequested] = useState(false);
  const displayName = user?.name ?? m.auth_common_guest();
  const displaySub = user?.email ?? m.auth_common_not_signed_in();

  if (menuRequested) {
    return (
      <Suspense
        fallback={
          <SidebarUserButton
            displayName={displayName}
            displaySub={displaySub}
            image={user?.image ?? null}
            onOpen={() => undefined}
          />
        }
      >
        <LazySidebarUserMenu user={user} defaultOpen />
      </Suspense>
    );
  }

  return (
    <SidebarUserButton
      displayName={displayName}
      displaySub={displaySub}
      image={user?.image ?? null}
      onOpen={() => setMenuRequested(true)}
    />
  );
}

function SidebarUserButton({
  displayName,
  displaySub,
  image,
  onOpen,
}: {
  displayName: string;
  displaySub: string;
  image: string | null;
  onOpen: () => void;
}) {
  return (
    <SidebarMenu className="border-t pt-4">
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          type="button"
          onClick={onOpen}
          className="bg-sidebar-accent/60 text-sidebar-accent-foreground hover:bg-sidebar-accent"
        >
          <SimpleAvatar image={image} name={displayName} />
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{displayName}</span>
            <span className="truncate text-xs text-muted-foreground">
              {displaySub}
            </span>
          </div>
          <IconSelector className="ml-auto size-4" />
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function SimpleAvatar({ image, name }: { image: string | null; name: string }) {
  if (image) {
    return (
      <img
        src={image}
        alt=""
        className="size-8 shrink-0 rounded-full border object-cover"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border bg-muted text-muted-foreground">
      <span className="sr-only">{name}</span>
      <IconUser className="size-4" />
    </span>
  );
}
